// Normalized API errors (US-883 · R-2) — every failure that leaves the useApi
// seam is an ApiError with the same shape, so consumers branch on `status`
// instead of parsing message strings (401 → auth, 404 → status polling,
// 0 → network down, 5xx → generic message).
//
// The one deliberate exception: an AbortError raised by request cancellation
// (US-883 · R-3) is re-thrown unwrapped by useApi — it is a caller decision,
// not an API failure, and hooks must be able to tell them apart.
export class ApiError extends Error {
  constructor({ status, message, detail = null, path }) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.detail = detail;
    this.path = path;
  }
}

// fetch itself rejected (network down, DNS, CORS): there is no HTTP status,
// so the normalized status is 0.
export const networkApiError = (path, cause) =>
  new ApiError({
    status: 0,
    message: `Network request to ${path} failed`,
    detail: cause && cause.message ? cause.message : null,
    path,
  });

// Non-2xx response: surface DRF's error body ({detail} or {error}) as the
// detail when the body is JSON; an unreadable body degrades to null.
export const httpApiError = (status, rawBody, path) => {
  let detail = null;
  if (rawBody) {
    try {
      const parsed = JSON.parse(rawBody);
      detail = (parsed && (parsed.detail ?? parsed.error)) ?? null;
    } catch {
      detail = null;
    }
  }
  return new ApiError({
    status,
    message: `Request to ${path} failed with status ${status}`,
    detail,
    path,
  });
};

// 2xx response whose body does not parse as JSON — distinguishable from an
// HTTP failure by its detail marker (R-2: invalid JSON body is its own type).
export const invalidJsonApiError = (status, path) =>
  new ApiError({
    status,
    message: `Invalid JSON in response from ${path}`,
    detail: "invalid-json-body",
    path,
  });
